import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, BookOpen, Shield } from 'lucide-react';
interface OnboardingScreenProps {
  onComplete: () => void;
}
const slides = [{
  icon: Heart,
  title: 'Share Your Heart',
  description: 'Express how you feel in your own words. No judgment, just understanding and support.',
  color: 'from-rose-400 to-pink-400'
}, {
  icon: BookOpen,
  title: "Receive God's Word",
  description: 'Get personalized Bible verses, reflections, prayers, and uplifting music tailored to your mood.',
  color: 'from-sky-400 to-blue-400'
}, {
  icon: Shield,
  title: 'Your Privacy Matters',
  description: 'Your conversations are private and secure. We handle your data with care and respect.',
  color: 'from-purple-400 to-indigo-400'
}];
export function OnboardingScreen({
  onComplete
}: OnboardingScreenProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };
  const handleSkip = () => {
    onComplete();
  };
  const IconComponent = slides[currentSlide].icon;
  return <div className="min-h-screen bg-gradient-to-b from-white to-sky-50 flex flex-col" data-id="element-147">
      {/* Skip Button */}
      <div className="flex justify-end p-4" data-id="element-148">
        <button onClick={handleSkip} className="text-slate-600 hover:text-slate-800 font-medium px-4 py-2 rounded-lg hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-149">
          Skip
        </button>
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12" data-id="element-150">
        <AnimatePresence mode="wait" data-id="element-151">
          <motion.div key={currentSlide} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} className="w-full max-w-md text-center" data-id="element-152">
            {/* Icon */}
            <div className={`w-24 h-24 mx-auto mb-8 bg-gradient-to-br ${slides[currentSlide].color} rounded-full flex items-center justify-center shadow-lg`} data-id="element-153">
              <IconComponent className="w-12 h-12 text-white" data-id="element-154" />
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-slate-800 mb-4" data-id="element-155">
              {slides[currentSlide].title}
            </h2>

            {/* Description */}
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-156">
              {slides[currentSlide].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress Dots */}
      <div className="flex justify-center gap-2 mb-6" data-id="element-157">
        {slides.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-sky-400 ${index === currentSlide ? 'w-8 bg-sky-400' : 'w-2 bg-slate-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-158" />)}
      </div>

      {/* Next/Get Started Button */}
      <div className="px-6 pb-8" data-id="element-159">
        <button onClick={handleNext} className="w-full max-w-md mx-auto block px-6 py-4 bg-gradient-to-r from-sky-400 to-purple-400 text-white font-semibold rounded-xl hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2" data-id="element-160">
          {currentSlide < slides.length - 1 ? 'Next' : 'Get Started'}
        </button>
      </div>
    </div>;
}